import {Component, OnInit, ViewChild} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {FilterState} from '../../generic/classes/filter.state';
import {Message} from '../../generic/classes/message';
import {ProductDetail} from '../../generic/classes/product.detail';
import {ProductPrices} from '../../generic/classes/product.prices';
import {ProductTypes} from '../../generic/classes/product.types.enum';
import {ShoppingCartLine} from '../../generic/classes/shoppingcartline';
import {Logger} from '../../generic/logging/default-log.service';
import {ApplicationService} from '../../generic/service/application.service';
import {BaseService} from '../../generic/service/base.service';
import {FilterService} from '../../generic/service/filter.service';
import {ProductService} from '../../generic/service/product.service';
import {ShoppingCartService} from '../../generic/service/shoppingcart.service';
import {TagManagerService} from '../../generic/service/tag.manager.service';
import {UserService} from '../../generic/service/user.service';
import {WindowRef} from '../../generic/service/window-ref.service';
import {RenderProductImageCarouselComponent} from './render.product.image.carousel.component';
import {ProductOrderHistoryComponent} from './product.order.history.component';
import {GrowerProductsComponent} from './grower.products.component';
import {NotAvailableAlternativeProductsComponent} from './not.available.alternative.products.component';
import {AlternativeProductsComponent} from './alternative.products.component';
import {ProductdetailGrowerBlogComponent} from './productdetail.grower.blog.component';
import {ProductDetailFeaturesComponent} from './productdetail.features.component';
import {ProductDetailLabelsComponent} from './productdetail.labels.component';
import {ProductDetailProductPriceComponent} from './productdetail.product.price.component';
import {RenderProductThumbnailCarouselComponent} from './render.product.thumbnail.carousel.component';
import {ProductDetailExtraInformationComponent} from './productdetail.extra.information.component';

export const productDetailRoute: string = 'products/detail';

@Component({
	selector: 'product-detail',
	templateUrl: 'productdetail.component.html',
	styleUrls: [
		'./productdetail.component.scss',
		'product.order.history.component.scss',
	],
	standalone: true,
	imports: [
		RouterLink,
		RenderProductImageCarouselComponent,
		RenderProductThumbnailCarouselComponent,
		ProductDetailProductPriceComponent,
		ProductDetailLabelsComponent,
		ProductDetailFeaturesComponent,
		ProductdetailGrowerBlogComponent,
		AlternativeProductsComponent,
		NotAvailableAlternativeProductsComponent,
		GrowerProductsComponent,
		ProductOrderHistoryComponent,
		ProductDetailExtraInformationComponent
	],
})
export class ProductDetailComponent implements OnInit {
	private static winRef: WindowRef = new WindowRef();
	public productDetail: ProductDetail;
	unitPriceWithDiscount: number;
	largeImage: string;
	origin: string;
	type: string;
	itemNo: string;
	potSizeFrom: string;
	potSizeTill: string;
	potHeightFrom: string;
	potHeightTill: string;
	productPrices: ProductPrices;
	anchorValue: string;

	@ViewChild(RenderProductImageCarouselComponent)
	private productImageCarousel: RenderProductImageCarouselComponent;

	constructor(
		public applicationService: ApplicationService,
		public userService: UserService,
		private productService: ProductService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private shoppingCartService: ShoppingCartService,
		private filterService: FilterService,
		private titleService: Title,
		private metaService: Meta,
		private tagManagerService: TagManagerService,
		private logger: Logger,
	) {
	}

	get activeLine(): ShoppingCartLine | null {
		if (!this.userService.getUser()) {
			return null;
		}

		const shoppingCartLines =
			this.shoppingCartService.activeShoppingCartLines;

		if (!shoppingCartLines) {
			return null;
		}

		const foundShoppingCartLines = shoppingCartLines.find(
			line =>
				line.priceListLineNo === this.productDetail.priceListLineNo &&
				line.priceListNo === this.productDetail.priceListNo,
		);

		return foundShoppingCartLines === undefined ? null : foundShoppingCartLines;
	}

	/**
	 * Subscribe to the params from the activated route
	 */
	ngOnInit() {
		this.activatedRoute.params.subscribe(params => {
			this.origin = params.origin;
			this.type = params.type;
			this.itemNo = params.itemNo;
			this.potSizeFrom = params.potSizeFrom;
			this.potSizeTill = params.potSizeTill;
			this.potHeightFrom = params.potHeightFrom;
			this.potHeightTill = params.potHeightTill;
			this.getProduct(
				params.type,
				params.priceListNo,
				params.priceListLineNo,
			);
		});
	}

	triggerGoToSlide(index: number) {
		this.productImageCarousel.goToSlide(index);
	}

	getProduct(type: number, priceListNo: string, priceListLineNo: string) {
		const filterState: FilterState = this.filterService.getFilterState();
		const productsNotFoundTranslation = $localize`:@@requested.product.not.available:The requested product is not available`;
		const productsNotFoundErrorMessage = new Message(
			productsNotFoundTranslation,
			'warning',
			false,
			false,
		);

		/**
		 * Prevent calling the API if no priceListNo or priceListLineNo is given
		 */
		if (priceListLineNo.length === 0 || priceListNo.length === 0) {
			this.applicationService.message = productsNotFoundErrorMessage;
			return;
		}

		this.productService
			.getSingle(filterState, type, priceListNo, priceListLineNo)
			.subscribe(
				data => {
					this.setAttributes(
						data,
						type,
						priceListNo,
						priceListLineNo,
						filterState,
					);
					this.setMetaData(data);
				},
				error => {
					if (BaseService.is404Error(error)) {
						this.applicationService.message =
							productsNotFoundErrorMessage;
					}
				},
			);
	}

	setAttributes(
		product: ProductDetail,
		type: number,
		priceListNo: string,
		priceListLineNo: string,
		filterState: FilterState,
	) {
		this.productDetail = product;
		this.anchorValue = this.productService.getAnchorValue(
			type,
			priceListNo,
			priceListLineNo,
		);

		if (!product) {
			this.logger.error(
				'Error',
				`No product details found for (type=${type}, No=${priceListNo}, LineNo=${priceListLineNo})`,
			);
			return;
		}

		this.unitPriceWithDiscount = this.productDetail.unitDiscountPrice;
		this.largeImage = this.productService.getImage(product.image);

		this.productPrices = new ProductPrices(
			this.productDetail,
			this.getQuantity(),
		);

		this.tagManagerService.trackProductDetailView(product);
	}

	/**
	 * Sets the page metadata for better integration with search engines.
	 * @param product
	 */
	setMetaData(product: ProductDetail) {
		const shopTitle =
			ProductDetailComponent.winRef.nativeWindow.shopSettings.name;
		const productPageTitle = shopTitle
			? `${product.totalDescription1} | ${shopTitle}`
			: `${product.totalDescription1}`;

		this.titleService.setTitle(`${productPageTitle}`);

		if (
			product.totalDescription2 &&
			this.isEmptyString(product.totalDescription2)
		) {
			this.metaService.updateTag({
				name: 'description',
				content: `${product.totalDescription2}`,
			});
		} else {
			const genericDescription = $localize`:@@product.detail.generic.meta.description:${this.productDetail.totalDescription1} can be bought using your ${shopTitle} account. Register your business now to order from our webshop.`;
			this.metaService.updateTag({
				name: 'description',
				content: `${genericDescription}`,
			});
		}

		if (product.features) {
			this.metaService.updateTag({
				name: 'keywords',
				content: `${product.features}`,
			});
		}
	}

	isProductSoldOut(): boolean {
		return parseInt(this.type) === ProductTypes.SOLD_OUT;
	}

	getQuantity(): number {
		if (this.activeLine) {
			const quantity =
				this.activeLine.quantity / this.activeLine.qtyByBox;

			return Math.round(quantity);
		}

		return 0;
	}

	/**
	 * Method is called by the 'Back to cart', 'Back to planthunter' or some other back link.
	 */
	backToPrevious(): void {
		window.history.back();
	}

	backToProductsAndOpenBarcodeScanner() {
		return this.router.navigateByUrl(`/products;origin=${this.origin}`);
	}

	/**
	 * Take the quantity already ordered into account
	 *
	 * @returns {number}
	 */
	public getAvailableQuantity(): number {
		return this.productService.getAvailableQuantity(
			this.productDetail,
			this.shoppingCartService.activeShoppingCartLines,
		);
	}

	/**
	 * Checks whether additional images are supplied for the current Product
	 */
	public hasAdditionalImages(): boolean {
		return this.getAdditionalImages().length > 1;
	}

	/**
	 * Check if there are addition images. Otherwise, return the product image as an array
	 * to the carousel, so it renders one image correctly.
	 *
	 * The original AdditionalImages string will be formatted like: img1|img2|img3
	 * This method converts the additionalImages into an array of strings
	 */
	public getAdditionalImages(): string[] {
		if (this.productDetail.additionalImages) {
			return this.productDetail.additionalImages.trim().split('|');
		}

		return new Array(this.largeImage);
	}

	getPlantscapingFilter(): number {
		const plantscapingFilter =
			this.filterService.getFilterState().plantscaping;

		if (plantscapingFilter === undefined) {
			return 0;
		}

		return plantscapingFilter;
	}

	/**
	 * Checks the given string if it still holds any information when we remove all
	 * whitespaces. If not then we can assume it's an empty string.
	 *
	 * @param text
	 * @private
	 */
	private isEmptyString(text: string): boolean {
		return text.replace(/\s/g, '').length > 0;
	}
}
